/**
 * Widget Configuration
 *
 * Central configuration for the Predictor Widget.
 * Supports runtime configuration via data attributes on the script tag.
 */

import {
  defaultWidgetPosition,
  defaultWidgetSurface,
  type WidgetPosition,
} from "./utils/constants";
import { type WidgetView } from "./types";

/**
 * Widget configuration options.
 */
export interface WidgetConfig {
  /** Enable debug logging to console */
  debug: boolean;
  /** URL for "Explore Markets" button */
  exploreMarketsUrl: string;
  /** Desktop anchor for the floating widget; mobile is always bottom-center */
  position: WidgetPosition;
  /** Surface the widget renders into on load; set via `data-default-view`. */
  defaultView: WidgetView;
  /**
   * Opt-in flag for page-content scraping (title, headings, main body text,
   * JSON-LD). When false (default) the widget skips the ingestion POST and
   * only uses cached recommendations + the featured-markets fallback.
   * Publishers opt in via `data-allow-content-ingestion="true"` on the
   * script tag.
   */
  allowContentIngestion: boolean;
  /**
   * Publisher API key. Forwarded on the web-app deep link so the web app can
   * resolve the originating partner server-side and attribute the signup to
   * partner_widget.
   */
  apiKey?: string;
}

/**
 * API base URL, baked in at build time via tsup define.
 * Determined by WIDGET_ENV → env.config.ts mapping.
 */
// eslint-disable-next-line no-undef, turbo/no-undeclared-env-vars -- Replaced at build time by tsup define
export const apiBaseUrl: string = process.env.API_URL ?? "";

/** Web app base URL, baked in at build time via tsup define. */
export const webAppUrl: string = process.env.WEB_APP_URL ?? ""; // eslint-disable-line no-undef, turbo/no-undeclared-env-vars -- Replaced at build time by tsup define

/** App version (shared with the API) from @repo/utils/version, baked in at build time. */
// eslint-disable-next-line no-undef, turbo/no-undeclared-env-vars -- Replaced at build time by tsup define (not a real env var)
export const widgetVersion: string = process.env.WIDGET_VERSION ?? "0.0.0";

/** Widget build environment, baked in at build time. */
// eslint-disable-next-line no-undef -- Replaced at build time by tsup define
export const widgetEnv: string = process.env.WIDGET_ENV ?? "dev";

/**
 * Default configuration values.
 */
const defaultConfig: WidgetConfig = {
  debug: false,
  exploreMarketsUrl: `${webAppUrl}/market-event`,
  position: defaultWidgetPosition,
  defaultView: defaultWidgetSurface,
  allowContentIngestion: false,
};

/**
 * Current widget configuration (singleton).
 */
let currentConfig: WidgetConfig = { ...defaultConfig };

/**
 * Initialize configuration with user-provided options.
 * Merges with defaults, so partial config is allowed.
 */
export function initConfig(userConfig?: Partial<WidgetConfig>): WidgetConfig {
  currentConfig = {
    ...defaultConfig,
    ...userConfig,
  };
  return currentConfig;
}

/**
 * Get the current widget configuration.
 */
export function getConfig(): WidgetConfig {
  return currentConfig;
}

/**
 * Override just the resolved mount position after init. Used by the loader's
 * inline-fallback path so the Preact surfaces render with the floating layout
 * they actually mounted into, not the requested `inline` layout.
 */
export function setConfigPosition(position: WidgetPosition): void {
  currentConfig = { ...currentConfig, position };
}

/**
 * Check if debug mode is enabled.
 */
export function isDebugEnabled(): boolean {
  return currentConfig.debug;
}

/**
 * Log a message if debug mode is enabled.
 */
export function debugLog(message: string, ...args: unknown[]): void {
  if (currentConfig.debug) {
    // eslint-disable-next-line no-console -- Intentional: Debug logging
    console.log(`[pw] ${message}`, ...args);
  }
}

/**
 * Log a warning (always shown, regardless of debug mode).
 */
export function warnLog(message: string, ...args: unknown[]): void {
  // eslint-disable-next-line no-console -- Intentional: Warning logging
  console.warn(`[pw] ${message}`, ...args);
}

/**
 * Log an error (always shown, regardless of debug mode).
 */
export function errorLog(message: string, ...args: unknown[]): void {
  // eslint-disable-next-line no-console -- Intentional: Error logging
  console.error(`[pw] ${message}`, ...args);
}
